import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { fetchApiUsageStatsByApiKey } from '../../apis/apiUsageStats';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

function prepareLineChartData(timeline, timeframe) {
    // timeline: [{ time_bucket, total_count }]
    return {
        labels: timeline.map((d) => {
            const date = new Date(d.time_bucket);
            if (timeframe === 'hour') {
                // Show only time (HH:MM AM/PM)
                return date.toLocaleString(undefined, {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                });
            } else {
                // Show 'Mon DD'
                return date.toLocaleString(undefined, {
                    month: 'short',
                    day: '2-digit',
                });
            }
        }),
        datasets: [
            {
                label: 'Total Count',
                data: timeline.map((d) => d.total_count),
                borderColor: '#60A5FA', // soft blue
                backgroundColor: '#DBEAFE',
                fill: true,
                tension: 0.3,
                pointRadius: 3,
            },
        ],
    };
}

// Helper to get start/end dates for a given dateRange
function getDateRange(dateRange) {
    const now = new Date();
    let start, end;
    switch (dateRange) {
        case 'today':
            start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            end = new Date(start);
            end.setDate(start.getDate() + 1);
            break;
        case 'yesterday':
            end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            start = new Date(end);
            start.setDate(end.getDate() - 1);
            break;
        case 'this_week': {
            const day = now.getDay() || 7;
            start = new Date(now);
            start.setDate(now.getDate() - day + 1);
            start.setHours(0, 0, 0, 0);
            end = new Date(now);
            end.setDate(start.getDate() + 7);
            break;
        }
        case 'this_month':
            start = new Date(now.getFullYear(), now.getMonth(), 1);
            end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
            break;
        case 'this_quarter': {
            const quarter = Math.floor(now.getMonth() / 3);
            start = new Date(now.getFullYear(), quarter * 3, 1);
            end = new Date(now.getFullYear(), quarter * 3 + 3, 1);
            break;
        }
        case 'this_year':
            start = new Date(now.getFullYear(), 0, 1);
            end = new Date(now.getFullYear() + 1, 0, 1);
            break;
        default:
            // fallback to this week
            start = new Date(now);
            start.setDate(now.getDate() - (now.getDay() || 7) + 1);
            end = new Date(now);
            end.setDate(start.getDate() + 7);
            break;
    }
    // Format as YYYY-MM-DD
    return {
        start: start.toISOString().slice(0, 10),
        end: end.toISOString().slice(0, 10),
    };
}

const APIKeyUsages = () => {
    // Filter state
    const [apiKey, setApiKey] = useState('all');
    const [timeframe, setTimeframe] = useState('hour'); // default to hour
    const [dateRange, setDateRange] = useState('today'); // default to today

    // Timeline API state
    const [timeline, setTimeline] = useState([]);
    const [timelineLoading, setTimelineLoading] = useState(false);
    const [timelineError, setTimelineError] = useState(null);

    // Validation: if timeframe is hour, only allow today/yesterday
    useEffect(() => {
        if (timeframe === 'hour' && dateRange !== 'today' && dateRange !== 'yesterday') {
            setDateRange('today');
        }
    }, [timeframe, dateRange]);

    // Fetch timeline data when filters change
    useEffect(() => {
        const allowedRanges = timeframe === 'hour' ? ['today', 'yesterday'] : undefined;
        if (allowedRanges && !allowedRanges.includes(dateRange)) return;
        setTimelineLoading(true);
        setTimelineError(null);
        const { start, end } = getDateRange(dateRange);
        fetchApiUsageStatsByApiKey(start, end, apiKey !== 'all' ? apiKey : undefined, timeframe)
            .then((res) => {
                setTimeline(res.data);
                setTimelineLoading(false);
            })
            .catch(() => {
                setTimelineError('Failed to load timeline data');
                setTimelineLoading(false);
            });
    }, [apiKey, timeframe, dateRange]);

    const lineData = prepareLineChartData(timeline, timeframe);
    const lineOptions = {
        responsive: true,
        plugins: {
            legend: { position: 'top' },
            title: { display: true, text: 'API Key Usage Timeline' },
            tooltip: { mode: 'index', intersect: false },
        },
        scales: {
            x: {
                title: { display: true, text: 'Time' },
                ticks: { font: { size: 10 }, maxRotation: 45, minRotation: 30 },
            },
            y: {
                title: { display: true, text: 'Total Count' },
                beginAtZero: true,
            },
        },
    };

    return (
        <div style={{ maxWidth: 900 }}>
            {/* Filters UI */}
            <div className="d-flex flex-wrap gap-3 align-items-center mb-4">
                <div>
                    <label className="form-label mb-0 me-2">Timeframe:</label>
                    <select
                        className="form-select d-inline-block w-auto"
                        value={timeframe}
                        onChange={(e) => setTimeframe(e.target.value)}
                    >
                        <option value="day">Day</option>
                        <option value="hour">Hour</option>
                        <option value="month">Month</option>
                    </select>
                </div>
                <div>
                    <label className="form-label mb-0 me-2">Date Range:</label>
                    <select
                        className="form-select d-inline-block w-auto"
                        value={dateRange}
                        onChange={(e) => setDateRange(e.target.value)}
                    >
                        <option value="today">Today</option>
                        <option value="yesterday" disabled={timeframe !== 'hour'}>
                            Yesterday
                        </option>
                        <option value="this_week" disabled={timeframe === 'hour'}>
                            This Week
                        </option>
                        <option value="this_month" disabled={timeframe === 'hour'}>
                            This Month
                        </option>
                        <option value="this_quarter" disabled={timeframe === 'hour'}>
                            This Quarter
                        </option>
                        <option value="this_year" disabled={timeframe === 'hour'}>
                            This Year
                        </option>
                    </select>
                </div>
            </div>
            {/* Line Chart */}
            <div className="mb-5">
                {timelineLoading ? (
                    <div className="text-muted">Loading timeline...</div>
                ) : timelineError ? (
                    <div className="text-danger">{timelineError}</div>
                ) : (
                    <Line data={lineData} options={lineOptions} />
                )}
            </div>
        </div>
    );
};

export default APIKeyUsages;
